/* eslint-disable import/prefer-default-export */

/**
 * Generate a SHA-256 hash of the given text using Web Crypto API.
 * This is more reliable than base64 encoding and provides better collision resistance.
 * @param text - The text to hash
 * @returns Promise<string> - The first 16 characters of the hex hash
 */
export const generateContentHash = async (text: string): Promise<string> => {
    try {
        // Convert text to UTF-8 bytes
        const encoder = new TextEncoder();
        const data = encoder.encode(text);

        // Generate SHA-256 hash
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);

        // Convert to hex string
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

        // Return first 16 characters for cache key (still provides 64 bits of entropy)
        return hashHex.substring(0, 16);
    } catch (error) {
        // Fallback to a simple hash if Web Crypto API is not available

        // Simple fallback hash function using djb2 algorithm
        let hash = 5381;
        for (let i = 0; i < text.length; i += 1) {
            const char = text.charCodeAt(i);
            hash = (hash * 33 + char) % 0x100000000; // Convert to 32-bit unsigned integer
        }

        // Convert to hex and pad to 16 characters
        const hash1 = Math.abs(hash).toString(16).padStart(8, '0');
        const hash2 = Math.abs(hash * 31)
            .toString(16)
            .padStart(8, '0');
        return hash1 + hash2;
    }
};
